import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, Trash2 } from 'lucide-react';
import { parseAsString, useQueryState } from 'nuqs';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { holidayInput } from '@barbeiropro/contracts';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Field } from '@/components/shared/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/shared/states';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api, errorMessage } from '@/lib/api';
import { useBranches } from '@/lib/org';
import { applyServerError, zodForm } from '../catalog/lib';
import { HoursEditor } from './hours-editor';
import { Section } from './section';

interface Holiday {
  id: string;
  branchId: string | null;
  date: string;
  name: string;
}

const fmtHoliday = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

export function HoursPage() {
  const branches = useBranches();
  const [branchParam, setBranchParam] = useQueryState('unidade', parseAsString);
  const branchId = branchParam ?? branches.data?.[0]?.id;

  if (branches.isLoading) return <LoadingState />;
  if (branches.error) return <ErrorState error={branches.error} onRetry={() => branches.refetch()} />;
  if (!branches.data?.length) return <EmptyState title="Cadastre uma unidade primeiro" description="O horário de funcionamento é definido por unidade." />;

  return (
    <div className="grid gap-10">
      <Section title="Horário de funcionamento" description="Dias e horas em que a unidade recebe clientes. Abra mais de um intervalo no mesmo dia para marcar a pausa de almoço. Fora desses horários a agenda não aceita agendamentos.">
        {branches.data.length > 1 && (
          <div className="grid max-w-xs gap-1.5">
            <Label htmlFor="hours-branch">Unidade</Label>
            <Select value={branchId} onValueChange={(v) => setBranchParam(v)}>
              <SelectTrigger id="hours-branch" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {branches.data.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {branchId && <HoursEditor key={branchId} branchId={branchId} />}
      </Section>
      <Holidays />
    </div>
  );
}

function Holidays() {
  const qc = useQueryClient();
  const branches = useBranches().data ?? [];
  const list = useQuery({ queryKey: ['org', 'holidays'], queryFn: () => api.get<Holiday[]>('/org/holidays') });
  const form = useForm<{ date: string; name: string; branchId: string }>({
    resolver: zodForm(holidayInput, (v: { date: string; name: string; branchId: string }) => ({ date: v.date, name: v.name, branchId: v.branchId === 'all' ? null : v.branchId })),
    defaultValues: { date: '', name: '', branchId: 'all' },
  });
  const { errors } = form.formState;
  const add = useMutation({
    mutationFn: (v: { date: string; name: string; branchId: string }) => api.post('/org/holidays', holidayInput.parse({ date: v.date, name: v.name, branchId: v.branchId === 'all' ? null : v.branchId })),
    onSuccess: () => {
      toast.success('Feriado salvo');
      form.reset({ date: '', name: '', branchId: 'all' });
      qc.invalidateQueries({ queryKey: ['org', 'holidays'] });
      qc.invalidateQueries({ queryKey: ['agenda'] });
    },
    onError: (e) => applyServerError(form, e),
  });
  const branchName = (id: string | null) => (id ? (branches.find((b) => b.id === id)?.name ?? 'Unidade') : 'Todas as unidades');

  return (
    <Section title="Feriados e dias fechados" description="Nestas datas a agenda fica fechada para novos agendamentos, na unidade escolhida ou em todas.">
      <form onSubmit={form.handleSubmit((v) => add.mutate(v))} className="grid items-start gap-3 rounded-lg border bg-card p-4 sm:grid-cols-[10rem_1fr_14rem_auto]" noValidate>
        <Field id="hol-date" label="Data" error={errors.date?.message}>
          <Input id="hol-date" type="date" aria-invalid={!!errors.date} {...form.register('date')} />
        </Field>
        <Field id="hol-name" label="Nome" error={errors.name?.message}>
          <Input id="hol-name" placeholder="Ex.: Natal" aria-invalid={!!errors.name} {...form.register('name')} />
        </Field>
        <Field id="hol-branch" label="Unidade">
          <Controller
            control={form.control}
            name="branchId"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger id="hol-branch" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as unidades</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </Field>
        <Button type="submit" className="sm:mt-[1.375rem]" disabled={add.isPending}>
          <CalendarPlus aria-hidden /> Adicionar feriado
        </Button>
      </form>
      {list.isLoading ? (
        <LoadingState />
      ) : list.error ? (
        <ErrorState error={list.error} onRetry={() => list.refetch()} />
      ) : list.data?.length === 0 ? (
        <EmptyState title="Nenhum feriado cadastrado" description="Cadastre as datas em que a barbearia não abre." />
      ) : (
        <ul className="divide-y rounded-lg border bg-card">
          {list.data?.map((h) => (
            <li key={h.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
              <div className="min-w-0">
                <p className="text-sm font-medium">
                  <span className="tabular mr-2">{fmtHoliday(h.date)}</span>
                  {h.name}
                </p>
                <p className="text-xs text-muted-foreground">{branchName(h.branchId)}</p>
              </div>
              <ConfirmDialog
                trigger={
                  <Button variant="ghost" size="icon" aria-label={`Remover feriado ${h.name}`}>
                    <Trash2 className="text-destructive" aria-hidden />
                  </Button>
                }
                title={`Remover "${h.name}"?`}
                description={`A agenda volta a aceitar agendamentos em ${fmtHoliday(h.date)}.`}
                confirmLabel="Remover feriado"
                onConfirm={async () => {
                  try {
                    await api.del(`/org/holidays/${h.id}`);
                    toast.success('Feriado removido');
                    qc.invalidateQueries({ queryKey: ['org', 'holidays'] });
                    qc.invalidateQueries({ queryKey: ['agenda'] });
                  } catch (e) {
                    toast.error(errorMessage(e));
                  }
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
